/**
 * Admin Panel Guard Component
 * Protects admin panel access (ADMIN role only, regardless of tier)
 */

import React, { useMemo } from 'react';
import { useFirebaseAuth } from '@/hooks/use-firebase-auth';
import { useSubscription } from '@/hooks/use-subscription';
import { hasRole } from '@/lib/normalize-user-data';
import { PanelRedirect } from '@/components/guards/PanelRedirect';

interface AdminPanelGuardProps {
  children: React.ReactNode;
}

export function AdminPanelGuard({ children }: AdminPanelGuardProps) {
  const { user } = useFirebaseAuth();
  const { subscriptionTier, planName } = useSubscription();
  
  // Memoize permission check to prevent constant re-renders
  const permissionCheck = useMemo(() => {
    // Get user roles for detailed checking
    const userRoles = user?.roles || user?.profile?.roles || user?.role || [];
    const normalizedRoles = Array.isArray(userRoles) ? userRoles : [userRoles];
    const isAdmin = hasRole(user, 'ADMIN');
    
    // Admin Panel Access Rules:
    // ✅ ADMIN role ONLY (regardless of subscription tier)
    // ❌ All other users (redirect to their appropriate panel)
    
    // Only log once when permission actually changes
    console.log('👑 AdminPanelGuard Permission Check (memoized):', {
      action: 'ADMIN_PANEL_ACCESS',
      RAW_USER_DATA: {
        subscriptionTier: subscriptionTier || undefined,
        roles: user?.roles,
        email: user?.email
      },
      PROCESSED_DATA: {
        subscriptionTier: subscriptionTier || 'FREE',
        planName,
        userRoles: normalizedRoles,
        isAdmin
      },
      reason: isAdmin 
        ? `✅ Access GRANTED - Admin role (tier: ${planName || 'Free'})`
        : `❌ Access DENIED - No Admin role, redirecting to appropriate panel`
    });
    
    return { isAdmin, normalizedRoles };
  }, [planName, subscriptionTier, user?.email, user?.id, user?.role, user?.roles]);

  // Only ADMIN role can access admin panel
  if (permissionCheck.isAdmin) {
    return <>{children}</>;
  }

  // Non-admin users get redirected to their appropriate panel
  console.log('👑 Redirecting non-admin user to appropriate panel');
  return <PanelRedirect />;
}
